import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Hand, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER_ROLE_LABELS } from "@/lib/roles";
import TaskDetailDialog, { DueBadge } from "@/components/volunteer/TaskDetailDialog";

export default function UpForGrabs() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    api.get(`/tasks/up-for-grabs?show_all=${showAll}`).then(({ data }) => setTasks(data)).catch(() => {});
  }, [showAll]);
  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    try {
      await api.post(`/tasks/${id}/claim`);
      toast.success("Task claimed — it's yours now!");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="volunteer-upforgrabs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="hf-eyebrow">Claimable work</span>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">Up for Grabs</h1>
          {user?.volunteer_role && (
            <p className="mt-1 text-sm text-[#7c7a6a]">
              Showing {showAll ? "all open tasks" : `tasks suited to ${VOLUNTEER_ROLE_LABELS[user.volunteer_role] || "you"}`}.
            </p>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#3f6670]" data-testid="upforgrabs-showall-toggle">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="h-4 w-4 accent-[#c97049]" />
          Show all open tasks
        </label>
      </div>

      {tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Hand className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No open tasks right now</p>
          <p className="text-sm text-[#7c7a6a]">Check back soon or toggle "show all".</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {tasks.map((t) => (
            <div key={t.id} className="rounded-2xl border border-[#e4d8c4] bg-white p-5" data-testid={`grab-task-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setDetailId(t.id)} className="text-left font-heading text-lg font-semibold text-[#00334a] hover:text-[#c97049]" data-testid={`grab-open-${t.id}`}>{t.title}</button>
                {t.suited_role && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#efe6d6] px-2 py-0.5 text-[10px] font-bold text-[#3f6670]">
                    <Sparkles className="h-3 w-3" /> {t.suited_role_label}
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[#7c7a6a]">
                <span>{t.project_name}{t.due_date ? ` · due ${t.due_date}` : ""}</span>
                <DueBadge task={t} />
              </div>
              {t.description && <p className="mt-2 text-sm text-[#5a5849]">{t.description}</p>}
              <Button onClick={() => claim(t.id)} className="mt-4 w-full rounded-full hf-btn-coral font-bold" data-testid={`task-claim-${t.id}`}>
                Claim this task
              </Button>
            </div>
          ))}
        </div>
      )}
      <TaskDetailDialog taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} onChanged={load} />
    </div>
  );
}
